import React, { useCallback, useMemo, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
    ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip,
    CartesianGrid, ReferenceDot,
} from "recharts";
import { API_BASE } from "../../apiBase";
import ChartBoardReadMe from "./ChartBoardReadMe";

const API = API_BASE;
const DATE_RANGES = ["1M", "3M", "6M", "YTD", "1Y", "3Y", "ALL"];
const PARTICIPANTS = ["Dealer", "Asset_Mgr", "Lev_Money", "Other_Rept", "NonRept"];
const P_LABELS = { Dealer: "Dealer", Asset_Mgr: "AM", Lev_Money: "LM", Other_Rept: "OthRept", NonRept: "NonRept" };
const POSITIONS = ["net_pctoi", "long_pctoi", "short_pctoi"];
const POS_LABELS = { net_pctoi: "Net %OI", long_pctoi: "Long %OI", short_pctoi: "Short %OI" };
const OI_POSITIONS = ["long", "short"];
const OI_POS_LABELS = { long: "Long", short: "Short" };
const OI_SPREAD_LABELS = { long: "# Long +Spread", short: "# Short +Spread" };
const CATEGORIES = ["Money Market", "Treasury Bonds", "Equities", "Currencies (FX)", "Crypto", "Commodities / Credits"];

const GREEN = "#DAF2D0";
const WHITE = "#fff";
const LINE_COLORS = [
    { left: "#dcdeda", right: "#7CBF72" },
    { left: "#C1F0C8", right: "#4A9E3F" },
    { left: "#6BBF59", right: "#4A9E3F" },
];

const DEFAULT_CARDS = [
    { id: 1, charts: [
        { left: { code: "099741", participant: "Asset_Mgr", metric: "net_pctoi" }, right: { code: "099741", participant: "Lev_Money", metric: "net_pctoi" } },
        { left: { code: "097741", participant: "Asset_Mgr", metric: "net_pctoi" }, right: { code: "097741", participant: "Lev_Money", metric: "net_pctoi" } },
        { left: { code: "098662", participant: "Asset_Mgr", metric: "net_pctoi" }, right: { code: "098662", participant: "Lev_Money", metric: "net_pctoi" } },
    ]},
    { id: 2, charts: [
        { left: { code: "133741", participant: "Asset_Mgr", metric: "net_pctoi" }, right: { code: "133741", participant: "Lev_Money", metric: "net_pctoi" } },
        { left: { code: "146021", participant: "Asset_Mgr", metric: "net_pctoi" }, right: { code: "146021", participant: "Lev_Money", metric: "net_pctoi" } },
        { left: { code: "1170E1", participant: "Asset_Mgr", metric: "net_pctoi" }, right: { code: "1170E1", participant: "Lev_Money", metric: "net_pctoi" } },
    ]},
];

const STORAGE_KEY = "cotpro-dashboard-state";
const STORAGE_VERSION = 7;

function loadSavedState() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (parsed.version !== STORAGE_VERSION) return null;
        return parsed;
    } catch { return null; }
}

function saveState(cards, active, dateRange) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ cards, active, dateRange, version: STORAGE_VERSION })); } catch {}
}

export default function CotProDashboard() {
    const saved = useRef(loadSavedState());
    const [contracts, setContracts] = useState([]);
    const [dateRange, setDateRange] = useState(saved.current?.dateRange || "1Y");
    const [cursorTime, setCursorTime] = useState(null);
    const [cursorX, setCursorX] = useState(null);
    const [active, setActive] = useState(saved.current?.active || 0);
    const [cards, setCards] = useState(saved.current?.cards || DEFAULT_CARDS);
    const chartsRef = useRef(null);
    const [showReadMe, setShowReadMe] = useState(false);

    // Persist to localStorage on change
    useEffect(() => { saveState(cards, active, dateRange); }, [cards, active, dateRange]);

    useEffect(() => {
        axios.get(`${API}/api/cot-pro/contracts`).then((r) => setContracts(r.data)).catch(() => {});
    }, []);

    const goTo = useCallback((i) => { if (i >= 0 && i < cards.length) setActive(i); }, [cards.length]);
    const card = cards[active];
    const catMap = useMemo(() => {
        const m = {};
        for (const c of contracts) { if (!m[c.category]) m[c.category] = []; m[c.category].push(c); }
        return m;
    }, [contracts]);
    const getName = (code) => contracts.find((c) => c.code === code)?.name || code;
    const getNum = (code) => contracts.find((c) => c.code === code)?.num || "";

    const updateChart = (ci, side, field, val) => {
        setCards((prev) => {
            const next = [...prev];
            const c = { ...next[active], charts: [...next[active].charts] };
            c.charts[ci] = { ...c.charts[ci], [side]: { ...c.charts[ci][side], [field]: val } };
            next[active] = c;
            return next;
        });
    };

    const handleMouse = useCallback((e) => {
        if (!chartsRef.current) return;
        const rect = chartsRef.current.getBoundingClientRect();
        setCursorX(e.clientX - rect.left);
    }, []);

    return (
        <div data-testid="cot-pro-dashboard" style={{ color: "var(--pord-teal)", fontSize: 14, fontFamily: "'DM Sans', sans-serif" }}>
            <div className="flex items-center gap-2 mb-4 flex-wrap">
                <span style={{ fontSize: 11, color: GREEN, marginRight: 6, fontWeight: 400 }}>Date Range</span>
                {DATE_RANGES.map((dr) => (
                    <button key={dr} data-testid={`dr-${dr}`} onClick={() => setDateRange(dr)} style={{
                        fontFamily: "'DM Sans', sans-serif", fontSize: 11, padding: "4px 12px", borderRadius: 999,
                        border: dateRange === dr ? `1px solid ${GREEN}` : "1px solid rgba(184,212,208,0.12)",
                        background: dateRange === dr ? "rgba(218,242,208,0.08)" : "transparent",
                        color: dateRange === dr ? GREEN : "rgba(255,255,255,0.4)",
                        cursor: "pointer", transition: "all 200ms",
                    }}>{dr}</button>
                ))}
                <div style={{ flex: 1 }} />
                <button onClick={() => setShowReadMe(true)}
                    data-testid="read-me-dashboard"
                    style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        padding: "6px 18px", borderRadius: 999,
                        background: "rgba(184,212,208,0.12)", border: "1px solid var(--pord-teal)",
                        color: "var(--pord-teal)", fontSize: 13, fontWeight: 600, letterSpacing: "0.05em",
                        cursor: "pointer", transition: "all 200ms",
                        boxShadow: "0 0 12px rgba(184,212,208,0.15)",
                        fontFamily: "'DM Sans', sans-serif",
                    }}>
                    READ ME
                </button>
            </div>
            <div className="flex items-center gap-3 mb-5">
                {cards.map((c, i) => (
                    <button key={c.id} data-testid={`card-dot-${i}`} onClick={() => goTo(i)} style={{
                        width: active === i ? 28 : 8, height: 8, borderRadius: 999,
                        background: active === i ? "var(--pord-teal)" : "rgba(184,212,208,0.2)",
                        border: "none", cursor: "pointer", transition: "all 250ms",
                    }} />
                ))}
                <span style={{ fontSize: 14, color: "var(--pord-teal)", fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.04em", marginLeft: 8 }}>
                    Card {active + 1} of {cards.length}
                </span>
                <div style={{ flex: 1 }} />
                <button onClick={() => goTo(active - 1)} disabled={active === 0} style={navBtn(active === 0)}>←</button>
                <button onClick={() => goTo(active + 1)} disabled={active === cards.length - 1} style={navBtn(active === cards.length - 1)}>→</button>
            </div>
            <div ref={chartsRef} onMouseMove={handleMouse} onMouseLeave={() => { setCursorX(null); setCursorTime(null); }}
                style={{ display: "flex", flexDirection: "column", gap: 10, position: "relative" }}>
                {cursorX != null && (
                    <div style={{ position: "absolute", top: 0, bottom: 0, left: cursorX, width: 1, background: "rgba(255,255,255,0.35)", pointerEvents: "none", zIndex: 10 }} />
                )}
                {card.charts.map((cfg, ci) => (
                    cfg.rightOi ? (
                        <OiChartRow key={`${active}-${ci}`} config={cfg} dateRange={dateRange} catMap={catMap}
                            getName={getName} getNum={getNum} cursorTime={cursorTime} onCursor={setCursorTime}
                            colors={LINE_COLORS[ci] || LINE_COLORS[0]}
                            onUpdate={(side, field, val) => updateChart(ci, side, field, val)} />
                    ) : (
                        <ChartRow key={`${active}-${ci}`} config={cfg} dateRange={dateRange} catMap={catMap}
                            getName={getName} getNum={getNum} cursorTime={cursorTime} onCursor={setCursorTime}
                            colors={LINE_COLORS[ci] || LINE_COLORS[0]}
                            onUpdate={(side, field, val) => updateChart(ci, side, field, val)} />
                    )
                ))}
            </div>
            {showReadMe && <ChartBoardReadMe onClose={() => setShowReadMe(false)} />}
        </div>
    );
}

/* ── Helper: find closest point to a timestamp ── */
function findClosest(data, t) {
    if (!t || !data.length) return null;
    let best = data[0], minD = Infinity;
    for (const p of data) { const d = Math.abs(p.t - t); if (d < minD) { best = p; minD = d; } }
    return best;
}

/* ── Standard Chart Row ── */
function ChartRow({ config, dateRange, catMap, getName, getNum, cursorTime, onCursor, colors, onUpdate }) {
    const [leftData, setLeftData] = useState(null);
    const [rightData, setRightData] = useState(null);
    const lastTRef = useRef(null);

    useEffect(() => {
        let retries = 0;
        const load = () => {
            axios.get(`${API}/api/cot-pro/series/${config.left.code}`, { params: { participant: config.left.participant, metric: config.left.metric } })
                .then((r) => setLeftData(r.data)).catch((err) => {
                    if (err.response?.status === 503 && retries < 30) { retries++; setTimeout(load, 2000); }
                });
        };
        load();
    }, [config.left.code, config.left.participant, config.left.metric]);

    useEffect(() => {
        if (!config.right) return;
        let retries = 0;
        const load = () => {
            axios.get(`${API}/api/cot-pro/series/${config.right.code}`, { params: { participant: config.right.participant, metric: config.right.metric } })
                .then((r) => setRightData(r.data)).catch((err) => {
                    if (err.response?.status === 503 && retries < 30) { retries++; setTimeout(load, 2000); }
                });
        };
        load();
    }, [config.right?.code, config.right?.participant, config.right?.metric]);

    const chartData = useMemo(() => {
        if (!leftData?.series || !rightData?.series) return [];
        const map = {};
        for (const p of leftData.series) map[p.date] = { date: p.date, t: new Date(p.date).getTime(), left: p.value, oi: p.oi };
        for (const p of rightData.series) {
            if (!map[p.date]) map[p.date] = { date: p.date, t: new Date(p.date).getTime() };
            map[p.date].right = p.value;
        }
        return applyDateFilter(Object.values(map).sort((a, b) => a.t - b.t), dateRange);
    }, [leftData, rightData, dateRange]);

    const cp = useMemo(() => findClosest(chartData, cursorTime), [cursorTime, chartData]);

    const lName = getName(config.left.code);
    const rName = config.right ? getName(config.right.code) : "";
    const lLabel = `${lName} (${P_LABELS[config.left.participant]})`;
    const rLabel = config.right ? `${rName} (${P_LABELS[config.right.participant]})` : "";
    const lSeries = leftData?.series || [];
    const rSeries = rightData?.series || [];
    const lLast = lSeries[lSeries.length - 1];
    const rLast = rSeries[rSeries.length - 1];
    const lPrior = lSeries.length >= 2 ? lSeries[lSeries.length - 2] : null;
    const rPrior = rSeries.length >= 2 ? rSeries[rSeries.length - 2] : null;
    // Use file-sourced change values (accounts for CFTC revisions)
    const lWeeklyChangeOi = leftData?.oi_change;
    const rWeeklyChangeOi = rightData?.oi_change;
    const lm = config.left.metric;
    const rm = config.right?.metric;
    const lWeeklyChangeCt = lm === "long_pctoi" ? leftData?.long_change : lm === "short_pctoi" ? leftData?.short_change : leftData?.net_change;
    const rWeeklyChangeCt = rm === "long_pctoi" ? rightData?.long_change : rm === "short_pctoi" ? rightData?.short_change : rightData?.net_change;
    const lPctOiPrior = leftData?.prior_pctoi;
    const rPctOiPrior = rightData?.prior_pctoi;

    return (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 390px", gap: 0, background: "rgba(184,212,208,0.02)", border: "1px solid rgba(184,212,208,0.06)", borderRadius: 10, overflow: "hidden" }}>
            <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column" }}>
                <div style={{ display: "flex", gap: 24, marginBottom: 8 }}>
                    <AxisSel side="left" config={config.left} catMap={catMap} color={colors.left} onUpdate={(f, v) => onUpdate("left", f, v)} large />
                    {config.right && <AxisSel side="right" config={config.right} catMap={catMap} color={colors.right} onUpdate={(f, v) => onUpdate("right", f, v)} large />}
                </div>
                <div style={{ width: "100%", flex: 1, minHeight: 300, background: "rgba(255,255,255,0.04)", borderRadius: 8, padding: "6px 0", border: "1px solid rgba(255,255,255,0.04)" }}>
                    {chartData.length === 0 ? (
                        <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--pord-muted)", fontSize: 11 }}>Loading...</div>
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={chartData} margin={{ top: 4, right: 14, bottom: 0, left: 4 }}>
                                <CartesianGrid stroke="rgba(255,255,255,0.04)" vertical={false} />
                                <XAxis dataKey="t" type="number" scale="time" domain={["dataMin", "dataMax"]}
                                    tickFormatter={(t) => new Date(t).toLocaleDateString("en-US", { month: "short" })}
                                    tickLine={false} axisLine={{ stroke: "rgba(255,255,255,0.06)" }} minTickGap={40}
                                    tick={{ fontSize: 10, fill: "rgba(255,255,255,0.55)" }} />
                                <YAxis yAxisId="l" tickLine={false} axisLine={false} width={52}
                                    tick={({ x, y, payload }) => {
                                        const v = Number(payload.value);
                                        const txt = isNaN(v) ? "" : `${Math.round(v)}%`;
                                        const color = v < 0 ? "#F7C7AC" : "rgba(255,255,255,0.85)";
                                        return <text x={x} y={y} dy={4} textAnchor="end" fill={color} style={{ fontSize: 12 }}>{txt}</text>;
                                    }} />
                                <YAxis yAxisId="r" orientation="right" tickLine={false} axisLine={false} width={52}
                                    tick={({ x, y, payload }) => {
                                        const v = Number(payload.value);
                                        const txt = isNaN(v) ? "" : `${Math.round(v)}%`;
                                        const color = v < 0 ? "#F7C7AC" : "rgba(255,255,255,0.85)";
                                        return <text x={x} y={y} dy={4} textAnchor="start" fill={color} style={{ fontSize: 12 }}>{txt}</text>;
                                    }} />
                                <Tooltip
                                    content={({ active, payload }) => {
                                        const t = active && payload?.[0]?.payload?.t;
                                        if (t && t !== lastTRef.current) { lastTRef.current = t; setTimeout(() => onCursor(t), 0); }
                                        if (!active || !payload?.length) return null;
                                        const p = payload[0]?.payload;
                                        if (!p) return null;
                                        return (
                                            <div style={tooltipBox}>
                                                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.55)", marginBottom: 5 }}>{p.date}</div>
                                                {p.left != null && <div style={{ fontSize: 12, color: colors.left, fontWeight: 500, marginBottom: 2 }}>{getNum(config.left.code)}. {P_LABELS[config.left.participant]} %OI: {p.left.toFixed(2)}%</div>}
                                                {p.right != null && config.right && <div style={{ fontSize: 12, color: colors.right, fontWeight: 500 }}>{getNum(config.right.code)}. {P_LABELS[config.right.participant]} %OI: {p.right.toFixed(2)}%</div>}
                                            </div>
                                        );
                                    }}
                                    cursor={{ stroke: "rgba(255,255,255,0.25)", strokeWidth: 1 }}
                                />
                                <Line yAxisId="l" type="monotone" dataKey="left" stroke={colors.left} strokeWidth={2} strokeOpacity={0.58} dot={false} connectNulls isAnimationActive={false} />
                                <Line yAxisId="r" type="monotone" dataKey="right" stroke={colors.right} strokeWidth={2} strokeOpacity={0.58} dot={false} connectNulls isAnimationActive={false} />
                                {cp && cp.left != null && <ReferenceDot yAxisId="l" x={cp.t} y={cp.left} r={5} fill={colors.left} stroke="rgba(255,255,255,0.6)" strokeWidth={2} isFront />}
                                {cp && cp.right != null && <ReferenceDot yAxisId="r" x={cp.t} y={cp.right} r={5} fill={colors.right} stroke="rgba(255,255,255,0.6)" strokeWidth={2} isFront />}
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                </div>
                {/* Data label strip — synced across all charts via shared cursorTime */}
                <div style={cp ? labelCardActive : labelCardIdle}>
                    {cp ? (
                        <>
                            <span style={{ color: "rgba(255,255,255,0.5)" }}>{cp.date}</span>
                            {cp.left != null && <span style={{ color: colors.left, fontWeight: 400, fontSize: 12 }}>{getNum(config.left.code)}. {P_LABELS[config.left.participant]} %OI: {cp.left.toFixed(1)}%</span>}
                            {cp.right != null && config.right && <span style={{ color: colors.right, fontWeight: 400, fontSize: 12 }}>{getNum(config.right.code)}. {P_LABELS[config.right.participant]} %OI: {cp.right.toFixed(1)}%</span>}
                        </>
                    ) : (
                        <span style={{ color: "rgba(255,255,255,0.2)", fontSize: 10 }}>hover chart for live values</span>
                    )}
                </div>
            </div>
            <div style={{ padding: "10px 16px", borderLeft: "1px solid rgba(184,212,208,0.06)", display: "flex", flexDirection: "column", gap: 6 }}>
                <RichBand pctiles={leftData?.percentiles} value={leftData?.latest_value} label={lLabel} side="left" color={colors.left} rank={leftData?.pctile_rank} />
                {config.right && <RichBand pctiles={rightData?.percentiles} value={rightData?.latest_value} label={rLabel} side="right" color={colors.right} rank={rightData?.pctile_rank} />}
                <div style={{ marginTop: 4 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "auto min-content min-content", gap: "3px 16px", fontSize: 15 }}>
                        <span /><span style={{ textAlign: "right", color: GREEN, fontSize: 13, fontWeight: 500, whiteSpace: "nowrap" }}>Left Axis</span><span style={{ textAlign: "right", color: GREEN, fontSize: 13, fontWeight: 500, whiteSpace: "nowrap" }}>Right Axis</span>
                        <span style={{ color: WHITE, whiteSpace: "nowrap" }}>%oi Prior Wk</span><TVal v={lPctOiPrior} fmt={fmtPct} /><TVal v={rPctOiPrior} fmt={fmtPct} />
                        <span style={{ color: WHITE, whiteSpace: "nowrap" }}>%oi Latest Wk</span><TVal v={leftData?.latest_value} fmt={fmtPct} /><TVal v={rightData?.latest_value} fmt={fmtPct} />
                    </div>
                </div>
            </div>
        </div>
    );
}

function ScaleInput({ label, value, onChange }) {
    return (
        <label style={{ display: "flex", alignItems: "center", gap: 3 }}>
            <span style={{ fontSize: 9, color: "rgba(255,255,255,0.3)" }}>{label}</span>
            <input value={value} onChange={(e) => onChange(e.target.value)} placeholder="auto"
                style={{ width: 46, fontSize: 10, padding: "3px 5px", background: "rgba(30,34,24,0.8)", border: "1px solid rgba(184,212,208,0.12)", borderRadius: 4, color: "rgba(218,242,208,0.8)", outline: "none" }} />
        </label>
    );
}


function OiChartRow({ config, dateRange, catMap, getName, getNum, cursorTime, onCursor, colors, onUpdate }) {
    const [leftData, setLeftData] = useState(null);

    useEffect(() => {
        let retries = 0;
        const load = () => {
            axios.get(`${API}/api/cot-pro/series/${config.left.code}`, { params: { participant: config.left.participant, metric: config.left.metric } })
                .then((r) => setLeftData(r.data)).catch((err) => {
                    if (err.response?.status === 503 && retries < 30) { retries++; setTimeout(load, 2000); }
                });
        };
        load();
    }, [config.left.code, config.left.participant, config.left.metric]);

    const chartData = useMemo(() => {
        if (!leftData?.series?.length) return [];
        const arr = leftData.series.map((p) => ({
            date: p.date,
            t: new Date(p.date).getTime(),
            oi: p.oi,
            ct: p.value,
            sp: p.spread,
        }));
        return applyDateFilter(arr.sort((a, b) => a.t - b.t), dateRange);
    }, [leftData, dateRange]);

    const contractNum = getNum(config.left.code);
    const lName = leftData ? getName(leftData.code) : "";
    const pLabel = leftData?.participant ? P_LABELS[leftData.participant] || leftData.participant : "";
    const mLabel = OI_POS_LABELS[config.left.metric] || "Net";
    const spreadLabel = OI_SPREAD_LABELS[config.left.metric] || "# Net (L+S)";

    const LINE_OI = "#ffffff";
    const LINE_CT = "#A8D8A0";
    const LINE_SP = "#5AAF4C";

    const fmtAxis = (v) => {
        const neg = v < 0;
        const abs = Math.abs(v);
        return abs >= 1000000 ? `${neg ? "-" : ""}${(abs / 1000000).toFixed(1)}M` : abs >= 1000 ? `${neg ? "-" : ""}${(abs / 1000).toFixed(0)}K` : v.toFixed(0);
    };

    const cp = useMemo(() => findClosest(chartData, cursorTime), [cursorTime, chartData]);

    const series = leftData?.series || [];
    const latest = series[series.length - 1];
    const latestCt = latest?.value;
    const latestSpread = latest?.spread;
    // Use file-sourced change values (accounts for CFTC revisions to prior week data)
    const weeklyChangeOi = leftData?.oi_change;
    const weeklyChangeCt = config.left.metric === "long" ? leftData?.long_change : config.left.metric === "short" ? leftData?.short_change : leftData?.net_change;
    const spreadChange = leftData?.spread_change;
    const longShortChange = config.left.metric === "long" ? leftData?.long_change : config.left.metric === "short" ? leftData?.short_change : leftData?.net_change;

    return (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 390px", gap: 0, background: "rgba(184,212,208,0.02)", border: "1px solid rgba(184,212,208,0.06)", borderRadius: 10, overflow: "hidden" }}>
            <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column" }}>
                <div style={{ display: "flex", gap: 12, marginBottom: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <OiAxisSel config={config.left} catMap={catMap} onUpdate={(f, v) => onUpdate("left", f, v)} large />
                </div>
                <div style={{ display: "flex", gap: 12, marginBottom: 6, alignItems: "center", fontSize: 11 }}>
                    <span style={{ display: "flex", gap: 4, alignItems: "center" }}><span style={{ width: 10, height: 3, borderRadius: 2, background: LINE_OI }} />OI</span>
                    <span style={{ display: "flex", gap: 4, alignItems: "center" }}><span style={{ width: 10, height: 3, borderRadius: 2, background: LINE_CT }} /># {mLabel}</span>
                    <span style={{ display: "flex", gap: 4, alignItems: "center" }}><span style={{ width: 10, height: 3, borderRadius: 2, background: LINE_SP }} />{spreadLabel}</span>
                </div>
                <div style={{ width: "100%", flex: 1, minHeight: 300, background: "rgba(255,255,255,0.04)", borderRadius: 8, padding: "6px 0", border: "1px solid rgba(255,255,255,0.04)" }}>
                    <ResponsiveContainer>
                        <LineChart data={chartData} margin={{ top: 4, right: 14, bottom: 0, left: 4 }}>
                            <CartesianGrid stroke="rgba(255,255,255,0.04)" vertical={false} />
                            <XAxis dataKey="t" type="number" scale="time" domain={["dataMin", "dataMax"]}
                                tickFormatter={(t) => new Date(t).toLocaleDateString("en-US", { month: "short" })}
                                tickLine={false} axisLine={{ stroke: "rgba(255,255,255,0.06)" }} minTickGap={40}
                                tick={{ fontSize: 10, fill: "rgba(255,255,255,0.55)" }} />
                            <YAxis yAxisId="l" tickLine={false} axisLine={false} width={52}
                                tick={({ x, y, payload }) => {
                                    const v = Number(payload.value);
                                    const clr = v < 0 ? "#F7C7AC" : "rgba(255,255,255,0.85)";
                                    return <text x={x} y={y} dy={4} textAnchor="end" fill={clr} style={{ fontSize: 12 }}>{isNaN(v) ? "" : fmtAxis(v)}</text>;
                                }} />
                            <YAxis yAxisId="r" orientation="right" tickLine={false} axisLine={false} width={52} tick={false} />
                            <Tooltip content={({ active, payload }) => {
                                if (!active || !payload?.length) return null;
                                const d = payload[0]?.payload;
                                if (d?.t) onCursor(d.t);
                                return null;
                            }} />
                            <Line yAxisId="l" type="monotone" dataKey="oi" stroke={LINE_OI} dot={false} strokeWidth={2} strokeOpacity={0.58} isAnimationActive={false} />
                            <Line yAxisId="l" type="monotone" dataKey="ct" stroke={LINE_CT} dot={false} strokeWidth={2} strokeOpacity={0.58} isAnimationActive={false} />
                            <Line yAxisId="l" type="monotone" dataKey="sp" stroke={LINE_SP} dot={false} strokeWidth={2} strokeOpacity={0.58} isAnimationActive={false} />
                            {cp && cp.oi != null && <ReferenceDot yAxisId="l" x={cp.t} y={cp.oi} r={5} fill={LINE_OI} stroke="rgba(255,255,255,0.6)" strokeWidth={2} isFront />}
                            {cp && cp.ct != null && <ReferenceDot yAxisId="l" x={cp.t} y={cp.ct} r={5} fill={LINE_CT} stroke="rgba(255,255,255,0.6)" strokeWidth={2} isFront />}
                            {cp && cp.sp != null && <ReferenceDot yAxisId="l" x={cp.t} y={cp.sp} r={5} fill={LINE_SP} stroke="rgba(255,255,255,0.6)" strokeWidth={2} isFront />}
                        </LineChart>
                    </ResponsiveContainer>
                </div>
                <div style={cp ? labelCardActive : labelCardIdle}>
                    {cp ? (
                        <>
                            <span style={{ color: "rgba(255,255,255,0.5)" }}>{cp.date}</span>
                            {cp.oi != null && <span style={{ color: LINE_OI, fontWeight: 400, fontSize: 12 }}>OI: {Math.round(cp.oi).toLocaleString()}</span>}
                            {cp.ct != null && <span style={{ color: LINE_CT, fontWeight: 400, fontSize: 12 }}>{contractNum ? `${contractNum}. ` : ""}{mLabel}: {Math.round(cp.ct).toLocaleString()}</span>}
                            {cp.sp != null && <span style={{ color: LINE_SP, fontWeight: 400, fontSize: 12 }}>{spreadLabel}: {Math.round(cp.sp).toLocaleString()}</span>}
                        </>
                    ) : (
                        <span style={{ color: "rgba(255,255,255,0.2)", fontSize: 10 }}>hover chart for live values</span>
                    )}
                </div>
            </div>
            <div style={{ padding: "10px 12px", borderLeft: "1px solid rgba(184,212,208,0.06)", display: "flex", flexDirection: "column", gap: 8 }}>
                <RichBand pctiles={leftData?.percentiles} value={leftData?.latest_value} label={`${lName} (${pLabel})`} side="right" color={LINE_CT} rank={leftData?.pctile_rank} />
                <div style={{ marginTop: 4 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "auto min-content", gap: "3px 16px", fontSize: 15 }}>
                        <span style={{ fontSize: 13, color: "var(--pord-muted)", whiteSpace: "nowrap" }}></span><span style={{ textAlign: "right", color: GREEN, fontSize: 13, fontWeight: 500, whiteSpace: "nowrap" }}>Values</span>
                        <span style={{ color: WHITE, whiteSpace: "nowrap" }}>Open Interest</span><TVal v={latest?.oi} fmt={fmtInt} color={WHITE} />
                        <span style={{ color: WHITE, whiteSpace: "nowrap" }}>w/w change - OI</span><TVal v={weeklyChangeOi} fmt={fmtChg2} color={WHITE} />
                    </div>
                    <hr style={{ border: "none", borderTop: "1px solid rgba(184,212,208,0.12)", margin: "6px 0" }} />
                    <div style={{ display: "grid", gridTemplateColumns: "auto min-content", gap: "3px 16px", fontSize: 15 }}>
                        <span style={{ color: WHITE, whiteSpace: "nowrap" }}>{spreadLabel}</span><TVal v={latestSpread} fmt={fmtInt} />
                        <span style={{ color: WHITE, whiteSpace: "nowrap" }}># Spread</span><TVal v={leftData?.spread_position} fmt={fmtInt} />
                        <span style={{ color: WHITE, whiteSpace: "nowrap" }}>Outright {mLabel}</span><TVal v={latestCt} fmt={fmtInt} />
                    </div>
                    <hr style={{ border: "none", borderTop: "1px solid rgba(184,212,208,0.12)", margin: "6px 0" }} />
                    <div style={{ display: "grid", gridTemplateColumns: "auto min-content", gap: "3px 16px", fontSize: 15 }}>
                        <span style={{ color: WHITE, whiteSpace: "nowrap" }}># Spread + Outright {mLabel} - Prior Week</span><TVal v={config.left.metric === "long" ? leftData?.prior_long_spread : config.left.metric === "short" ? leftData?.prior_short_spread : (leftData?.prior_long_spread != null && leftData?.prior_short_spread != null ? leftData.prior_long_spread - leftData.prior_short_spread : null)} fmt={fmtInt} />
                        <span style={{ color: WHITE, whiteSpace: "nowrap" }}># Spread + Outright {mLabel} - Latest Week</span><TVal v={latestSpread} fmt={fmtInt} />
                        <span style={{ color: WHITE, whiteSpace: "nowrap" }}>w/w change</span><TVal v={config.left.metric === "long" ? leftData?.change_long_spread : config.left.metric === "short" ? leftData?.change_short_spread : (leftData?.change_long_spread != null && leftData?.change_short_spread != null ? leftData.change_long_spread - leftData.change_short_spread : null)} fmt={fmtChg2} />
                    </div>
                </div>
            </div>
        </div>
    );
}

function AxisSel({ side, config, catMap, color, onUpdate, large }) {
    const s = large ? ddStyleLg : ddStyle;
    return (
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center", flex: 1 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />
            <select value={config.code} onChange={(e) => onUpdate("code", e.target.value)} style={{ ...s, flex: 1, minWidth: 0 }}>
                {CATEGORIES.map((cat) => <optgroup key={cat} label={cat}>{(catMap[cat] || []).map((c) => <option key={c.code} value={c.code}>{c.num}. {c.name}</option>)}</optgroup>)}
            </select>
            <select value={config.participant} onChange={(e) => onUpdate("participant", e.target.value)} style={{ ...s, width: large ? 72 : 62 }}>
                {PARTICIPANTS.map((p) => <option key={p} value={p}>{P_LABELS[p]}</option>)}
            </select>
            <select value={config.metric} onChange={(e) => onUpdate("metric", e.target.value)} style={{ ...s, width: large ? 86 : 72 }}>
                {POSITIONS.map((p) => <option key={p} value={p}>{POS_LABELS[p]}</option>)}
            </select>
        </div>
    );
}

function OiAxisSel({ config, catMap, onUpdate, large }) {
    const s = large ? ddStyleLg : ddStyle;
    return (
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center", flex: 1 }}>
            <select value={config.code} onChange={(e) => onUpdate("code", e.target.value)} style={{ ...s, flex: 1, minWidth: 0 }}>
                {CATEGORIES.map((cat) => <optgroup key={cat} label={cat}>{(catMap[cat] || []).map((c) => <option key={c.code} value={c.code}>{c.num}. {c.name}</option>)}</optgroup>)}
            </select>
            <select value={config.participant} onChange={(e) => onUpdate("participant", e.target.value)} style={{ ...s, width: large ? 72 : 62 }}>
                {PARTICIPANTS.map((p) => <option key={p} value={p}>{P_LABELS[p]}</option>)}
            </select>
            <select value={config.metric} onChange={(e) => onUpdate("metric", e.target.value)} style={{ ...s, width: large ? 72 : 62 }}>
                {OI_POSITIONS.map((p) => <option key={p} value={p}>{OI_POS_LABELS[p]}</option>)}
            </select>
        </div>
    );
}

function RichBand({ pctiles, value, label, side, color, rank }) {
    if (!pctiles || pctiles.p5 == null || pctiles.p95 == null) return null;
    const p5 = pctiles.p5 ?? 0;
    const p16 = pctiles.p16 ?? p5;
    const p50 = pctiles.p50 ?? ((p5 + (pctiles.p95 ?? 0)) / 2);
    const p84 = pctiles.p84 ?? pctiles.p95 ?? 0;
    const p95 = pctiles.p95 ?? 0;
    // Uniform scale: 5 markers evenly spaced at 0%, 25%, 50%, 75%, 100%
    // matching the space-between label layout underneath
    const p16Pct = 25;
    const p50Pct = 50;
    const p84Pct = 75;
    // Map rank to position on this even grid
    const toPos = (r) => {
        if (r <= 5) return 0;
        if (r <= 16) return (r - 5) / (16 - 5) * 25;
        if (r <= 50) return 25 + (r - 16) / (50 - 16) * 25;
        if (r <= 84) return 50 + (r - 50) / (84 - 50) * 25;
        if (r <= 95) return 75 + (r - 84) / (95 - 84) * 25;
        return 100;
    };
    const pctileRank = rank != null ? Math.round(rank) : null;
    const pos = pctileRank != null ? Math.max(2, Math.min(98, toPos(pctileRank))) : null;
    const fmt = (v) => v != null ? `${v >= 0 ? "+" : ""}${v.toFixed(1)}%` : "—";
    return (
        <div style={{ padding: "10px 12px", borderRadius: 8, background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.04)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
                <span style={{ fontSize: 12, color: WHITE, fontWeight: 400 }}>{label}</span>
                {pctileRank != null && value != null && (
                    <span style={{ fontSize: 12 }}>
                        <span style={{ color, fontWeight: 600, fontSize: 14 }}>{pctileRank}th</span>
                        <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 10 }}> pctile </span>
                        <span style={{ color, fontSize: 12 }}>{value >= 0 ? "+" : ""}{value.toFixed(1)}% OI</span>
                    </span>
                )}
            </div>
            <div style={{ position: "relative", height: 18, borderRadius: 9, overflow: "hidden", marginBottom: 6 }}>
                <div style={{ position: "absolute", inset: 0, background: "rgba(255,255,255,0.06)", borderRadius: 9 }} />
                <div style={{ position: "absolute", top: 0, bottom: 0, left: 0, width: `${p16Pct}%`, background: "rgba(220,160,150,0.18)" }} />
                <div style={{ position: "absolute", top: 0, bottom: 0, left: `${p16Pct}%`, width: `${p84Pct - p16Pct}%`, background: "rgba(218,242,208,0.10)" }} />
                <div style={{ position: "absolute", top: 0, bottom: 0, left: `${p84Pct}%`, right: 0, background: "rgba(220,160,150,0.18)" }} />
                <div style={{ position: "absolute", top: 0, bottom: 0, left: `${p16Pct}%`, width: 1, background: "rgba(255,255,255,0.12)" }} />
                <div style={{ position: "absolute", top: 0, bottom: 0, left: `${p50Pct}%`, width: 1, background: "rgba(255,255,255,0.12)" }} />
                <div style={{ position: "absolute", top: 0, bottom: 0, left: `${p84Pct}%`, width: 1, background: "rgba(255,255,255,0.12)" }} />
                {pos != null && <div style={{ position: "absolute", top: "50%", left: `${pos}%`, transform: "translate(-50%,-50%)", width: 14, height: 14, borderRadius: "50%", background: color, border: "2px solid rgba(255,255,255,0.5)", boxShadow: `0 0 6px ${color}` }} />}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "white", marginBottom: 2 }}>
                <span>5th</span><span>16th</span><span>50th</span><span>84th</span><span>95th</span>
            </div>
        </div>
    );
}

function TVal({ v, fmt, color }) { return <span style={{ textAlign: "right", color: color || GREEN, fontVariantNumeric: "tabular-nums", fontSize: 15, whiteSpace: "nowrap" }}>{v != null ? fmt(v) : "—"}</span>; }
function fmtInt(v) { return Math.round(v).toLocaleString(); }
function fmtPct(v) { return `${v.toFixed(2)}%`; }
function fmtChg2(v) { return `${v >= 0 ? "+" : ""}${Math.round(v).toLocaleString()}`; }

function applyDateFilter(arr, dr) {
    if (!arr.length) return arr;
    const last = arr[arr.length - 1].t;
    const days = { "1M": 30, "3M": 90, "6M": 180, "1Y": 365, "3Y": 1095 };
    if (dr === "YTD") { const j = new Date(new Date(last).getFullYear(), 0, 1).getTime(); return arr.filter((d) => d.t >= j); }
    if (dr === "ALL") return arr;
    return arr.filter((d) => d.t >= last - (days[dr] || 365) * 86400000);
}

const ddStyle = { fontFamily: "'DM Sans', sans-serif", fontSize: 11, padding: "5px 8px", background: "rgba(30,34,24,0.9)", border: "1px solid rgba(184,212,208,0.15)", borderRadius: 5, color: "rgba(218,242,208,0.9)", outline: "none" };
const ddStyleLg = { fontFamily: "'DM Sans', sans-serif", fontSize: 16, padding: "7px 10px", background: "rgba(30,34,24,0.9)", border: "1px solid rgba(184,212,208,0.15)", borderRadius: 6, color: "rgba(218,242,208,0.9)", outline: "none" };
const tooltipBox = { background: "rgba(20,24,16,0.92)", border: "1px solid rgba(184,212,208,0.18)", borderRadius: 6, padding: "8px 12px", minWidth: 130, backdropFilter: "blur(10px)", boxShadow: "0 4px 16px rgba(0,0,0,0.4)", fontVariantNumeric: "tabular-nums" };
const labelCardActive = { marginTop: 6, padding: "6px 12px", fontSize: 11, display: "flex", gap: 16, alignItems: "center", background: "rgba(20,24,16,0.85)", border: "1px solid rgba(184,212,208,0.12)", borderRadius: 6, backdropFilter: "blur(6px)" };
const labelCardIdle = { marginTop: 6, padding: "6px 12px", fontSize: 11, display: "flex", gap: 16, alignItems: "center" };
function navBtn(disabled) { return { background: "none", border: "1px solid rgba(184,212,208,0.12)", borderRadius: 6, padding: "5px 12px", color: disabled ? "rgba(255,255,255,0.12)" : "var(--pord-teal)", cursor: disabled ? "default" : "pointer", fontSize: 13 }; }
